// ✅ src/pages/VehicleV2/VehicleSubAssemblyMaster.jsx
// Visual design aligned with ProductionBOMListing.

import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowLeft, FaSave, FaTrash, FaPlus, FaTimes } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/subAssemblyMaster";

const getLS = (key, fallback = "") => {
  if (typeof window === "undefined") return fallback;
  const val = localStorage.getItem(key);
  return val !== null && val !== undefined ? val : fallback;
};

const VehicleSubAssemblyMaster = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const formRef = useRef(null);
  const inputRef = useRef(null);

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype") || "";
  const userId = getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const vehTypeFromUrl = Number(searchParams.get("vehicletypeid") || 0);
  const vehTypeFromSession = Number(
    sessionStorage.getItem("VehicleTypeID") ||
      sessionStorage.getItem("vehicletypeid") ||
      0
  );
  const initialVehicleTypeId = vehTypeFromUrl || vehTypeFromSession || 0;

  const [loading, setLoading] = useState(false);
  const [vehicleTypeLocked, setVehicleTypeLocked] = useState(true);
  const [editingId, setEditingId] = useState(0);
  const [vehicleTypes, setVehicleTypes] = useState([]);

  const [formData, setFormData] = useState({
    vehicleTypeId: 0,
    subAssemblyName: "",
  });

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);

  const totalPages = useMemo(
    () => (total ? Math.ceil(total / limit) : 0),
    [total, limit]
  );

  const fieldClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const pendingCount = pendingDeleteIds.length;
  const pendingNames = useMemo(() => {
    const ids = new Set(pendingDeleteIds.map((id) => Number(id)));
    return rows
      .filter((r) => ids.has(Number(r.subAssemblyId)))
      .map((r) => r.subAssemblyName)
      .filter(Boolean);
  }, [pendingDeleteIds, rows]);

  const fetchDropdowns = async (vehTypeId) => {
    const r = await axios.get(`${API_BASE}/dropdowns`, {
      params: { vehicleTypeId: vehTypeId || 0 },
      headers: commonHeaders,
    });

    if (!r.data?.success)
      throw new Error(r.data?.message || "Failed to load dropdowns");

    const types = Array.isArray(r.data.vehicleTypes) ? r.data.vehicleTypes : [];
    setVehicleTypes(types);

    const selected =
      Number(vehTypeId || 0) ||
      Number(r.data.selectedVehicleTypeId || 0) ||
      Number(types?.[0]?.id || 0);

    setFormData((p) => ({ ...p, vehicleTypeId: selected || 0 }));
    setVehicleTypeLocked(true);
    return selected || 0;
  };

  const fetchList = async (vehTypeId, pg = page, lim = limit) => {
    if (!vehTypeId) {
      setRows([]);
      setTotal(0);
      setSelectedIds(new Set());
      return;
    }

    const r = await axios.get(`${API_BASE}/list`, {
      params: { vehicleTypeId: vehTypeId, page: pg, limit: lim },
      headers: commonHeaders,
    });

    if (!r.data?.success)
      throw new Error(r.data?.message || "Failed to load list");

    setRows(Array.isArray(r.data.rows) ? r.data.rows : []);
    setTotal(Number(r.data.total || 0));
    setSelectedIds(new Set());
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        if (vehTypeFromUrl)
          sessionStorage.setItem("VehicleTypeID", String(vehTypeFromUrl));

        const selected = await fetchDropdowns(initialVehicleTypeId);
        await fetchList(selected, 1, limit);
        setPage(1);
      } catch (e) {
        console.error(e);
        toast.error(e.message || "Failed to initialize page");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehTypeFromUrl, commonHeaders]);

  useEffect(() => {
    (async () => {
      try {
        if (!formData.vehicleTypeId) return;
        setLoading(true);
        await fetchList(formData.vehicleTypeId, page, limit);
      } catch (e) {
        console.error(e);
        toast.error(e.message || "Failed to load list");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, commonHeaders]);

  const onChange = (e) => {
    const { name, value } = e.target;

    if (name === "vehicleTypeId") {
      const v = Number(value || 0);
      setFormData((p) => ({ ...p, vehicleTypeId: v }));
      setPage(1);
      return;
    }

    setFormData((p) => ({ ...p, [name]: value }));
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => inputRef.current?.focus(), 250);
  };

  const clearForm = () => {
    setEditingId(0);
    setFormData((p) => ({ ...p, subAssemblyName: "" }));
  };

  const startAddNew = () => {
    clearForm();
    scrollToForm();
  };

  const handleEdit = (row) => {
    setEditingId(Number(row.subAssemblyId || 0));
    setFormData((p) => ({
      ...p,
      subAssemblyName: row.subAssemblyName || "",
    }));
    scrollToForm();
  };

  const handleSave = async () => {
    const vehicleTypeId = Number(formData.vehicleTypeId || 0);
    const subAssemblyName = String(formData.subAssemblyName || "").trim();

    if (!vehicleTypeId) return toast.error("Please select Vehicle Type");
    if (!subAssemblyName) return toast.error("Please enter Vehicle Sub Assembly");

    try {
      setLoading(true);

      const payload = {
        vehicleTypeId,
        subAssemblyId: editingId ? editingId : 0,
        subAssemblyName,
        branchId: Number(branchId || 0),
        loginUserId: Number(userId || 0),
      };

      const r = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (!r.data?.success) {
        toast.error(r.data?.message || "Save failed");
        return;
      }

      toast.success(
        r.data?.message || (editingId ? "Updated Successfully" : "Saved Successfully")
      );
      clearForm();
      await fetchList(vehicleTypeId, page, limit);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || e.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (id) => {
    const rowId = Number(id || 0);
    if (!rowId) return;

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const toggleAll = () => {
    if (!rows.length) return;

    setSelectedIds((prev) => {
      if (prev.size === rows.length) return new Set();
      return new Set(rows.map((r) => Number(r.subAssemblyId)));
    });
  };

  const openDeleteConfirm = (idsFromArgs = null) => {
    const ids = Array.isArray(idsFromArgs)
      ? idsFromArgs
      : Array.from(selectedIds);

    if (!ids.length) {
      toast.error("Please select at least one row to delete");
      return;
    }

    setPendingDeleteIds(ids.map((id) => Number(id)).filter(Boolean));
    setConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setPendingDeleteIds([]);
  };

  const confirmDelete = async () => {
    const ids = pendingDeleteIds;
    if (!ids.length) return;

    try {
      setConfirmLoading(true);
      setLoading(true);

      const r = await axios.post(
        `${API_BASE}/delete`,
        { ids },
        { headers: commonHeaders }
      );

      if (!r.data?.success) {
        toast.error(r.data?.message || "Delete failed");
        return;
      }

      toast.success(r.data?.message || "Delete completed");
      if (editingId && ids.includes(editingId)) clearForm();
      setConfirmOpen(false);
      setPendingDeleteIds([]);
      await fetchList(formData.vehicleTypeId, page, limit);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || e.message || "Delete failed");
    } finally {
      setConfirmLoading(false);
      setLoading(false);
    }
  };

  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              VEHICLE SUB ASSEMBLY MASTER
            </span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <h2
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Vehicle Sub Assembly
              </h2>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Records
              </span>
              {editingId ? (
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isDark
                      ? "bg-amber-900 text-amber-100"
                      : "bg-amber-100 text-amber-800"
                  }`}
                >
                  Editing ID: {editingId}
                </span>
              ) : null}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={startAddNew}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                <FaArrowLeft />
                Back
              </button>
            </div>
          </div>

          <div
            ref={formRef}
            className={`p-6 rounded-lg mb-6 ${
              isDark ? "bg-gray-800/50" : "bg-blue-50"
            }`}
          >
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h3
                className={`text-lg font-semibold ${
                  isDark ? "text-white" : "text-blue-900"
                }`}
              >
                {editingId ? "Edit Vehicle Sub Assembly" : "Add Vehicle Sub Assembly"}
              </h3>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={clearForm}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                    isDark
                      ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-white"
                  }`}
                >
                  {editingId ? "Cancel" : "Clear"}
                </button>

                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    loading
                      ? "opacity-60 cursor-not-allowed bg-gray-400"
                      : `${buttonClass} ${hoverClass}`
                  } text-white`}
                >
                  <FaSave />
                  {loading
                    ? editingId
                      ? "Updating..."
                      : "Saving..."
                    : editingId
                    ? "Update"
                    : "Save"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelClass}>Vehicle Type</label>
                <select
                  name="vehicleTypeId"
                  value={formData.vehicleTypeId || 0}
                  onChange={onChange}
                  disabled={vehicleTypeLocked}
                  className={`${fieldClass} ${
                    vehicleTypeLocked ? "opacity-80 cursor-not-allowed" : ""
                  }`}
                >
                  <option value={0}>Select Vehicle Type</option>
                  {vehicleTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Vehicle Sub Assembly</label>
                <input
                  ref={inputRef}
                  name="subAssemblyName"
                  value={formData.subAssemblyName}
                  onChange={onChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSave();
                  }}
                  placeholder="Enter sub assembly..."
                  className={fieldClass}
                />
              </div>
            </div>
          </div>

          {selectedIds.size > 0 && (
            <div
              className={`rounded-lg p-4 mb-6 ${
                isDark
                  ? "bg-blue-900/30 border border-blue-700"
                  : "bg-blue-50 border border-blue-200"
              }`}
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <span
                  className={`font-semibold ${
                    isDark ? "text-blue-200" : "text-blue-800"
                  }`}
                >
                  Selected: {selectedIds.size}
                </span>

                <button
                  onClick={() => openDeleteConfirm()}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium bg-red-600 hover:bg-red-700"
                >
                  <FaTrash className="w-4 h-4" />
                  Delete Selected
                </button>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loading && rows.length === 0 ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Loading...
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  No sub assembly found.
                </div>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">
                      S.No
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">
                      <input
                        type="checkbox"
                        checked={
                          rows.length > 0 && selectedIds.size === rows.length
                        }
                        onChange={toggleAll}
                        className="w-4 h-4 cursor-pointer"
                      />
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">
                      Vehicle Sub Assembly
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">
                      Edit
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">
                      Delete
                    </th>
                  </tr>
                </thead>

                <tbody
                  className={`${
                    isDark ? "divide-gray-700" : "divide-gray-200"
                  } divide-y`}
                >
                  {rows.map((r, idx) => {
                    const id = Number(r.subAssemblyId);
                    const checked = selectedIds.has(id);
                    const isEditingRow = editingId && id === editingId;

                    return (
                      <tr
                        key={id}
                        className={`${
                          isEditingRow
                            ? isDark
                              ? "bg-amber-900/30"
                              : "bg-amber-50"
                            : idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-white"
                            : isDark
                            ? "bg-gray-750"
                            : "bg-gray-50"
                        } transition-colors ${
                          isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                        }`}
                      >
                        <td
                          className={`px-6 py-4 text-sm ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {(page - 1) * limit + idx + 1}
                        </td>

                        <td className="px-6 py-4 text-center">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleRow(id)}
                            className="w-4 h-4 cursor-pointer"
                          />
                        </td>

                        <td
                          className={`px-6 py-4 text-sm font-medium ${
                            isDark ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {r.subAssemblyName}
                        </td>

                        <td className="px-6 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => handleEdit(r)}
                            className="text-yellow-500 hover:text-yellow-600 text-lg transition"
                            title="Edit"
                          >
                            ✏️
                          </button>
                        </td>

                        <td className="px-6 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => openDeleteConfirm([id])}
                            className="text-red-500 hover:text-red-600 transition"
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              Page <span className="font-semibold">{page}</span> of{" "}
              <span className="font-semibold">{totalPages || 1}</span> • Total{" "}
              <span className="font-semibold">{total}</span>
            </div>

            <div className="flex items-center gap-2">
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className={`px-3 py-2 rounded-lg border ${
                  isDark
                    ? "bg-gray-800 border-gray-700 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>

              <div className="inline-flex rounded-lg shadow-sm overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  disabled={!canPrev || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={`px-4 py-2 text-sm font-medium ${
                    !canPrev || loading
                      ? "text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "text-white hover:bg-gray-800"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Prev
                </button>

                <button
                  disabled={!canNext || loading}
                  onClick={() => setPage((p) => p + 1)}
                  className={`px-4 py-2 text-sm font-medium ${
                    !canNext || loading
                      ? "text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "text-white hover:bg-gray-800"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeDeleteConfirm}
          />

          <div
            className={`relative w-full max-w-md rounded-2xl shadow-2xl border p-6 ${
              isDark
                ? "bg-gradient-to-br from-white/15 to-gray-900/90 backdrop-blur-md border-white/10 text-white"
                : "bg-white border-gray-200 text-gray-900"
            }`}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-11 h-11 rounded-full bg-red-600/15 text-red-600">
                  <FaTrash />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Confirm Delete</h3>
                  <p
                    className={`text-sm mt-1 ${
                      isDark ? "text-gray-300" : "text-gray-500"
                    }`}
                  >
                    This action cannot be undone.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={closeDeleteConfirm}
                disabled={confirmLoading}
                className={`p-2 rounded-lg ${
                  isDark ? "hover:bg-white/10" : "hover:bg-gray-100"
                }`}
              >
                <FaTimes />
              </button>
            </div>

            <div
              className={`rounded-lg p-4 mb-6 ${
                isDark ? "bg-gray-800/70" : "bg-blue-50"
              }`}
            >
              <p className={`text-sm font-medium ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                Delete {pendingCount} selected Sub Assembl
                {pendingCount === 1 ? "y" : "ies"}?
              </p>
              {pendingNames.length > 0 && (
                <p
                  className={`text-sm mt-2 ${
                    isDark ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  {pendingNames.slice(0, 5).join(", ")}
                  {pendingNames.length > 5
                    ? ` +${pendingNames.length - 5} more`
                    : ""}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeDeleteConfirm}
                disabled={confirmLoading}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                  isDark
                    ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                } ${confirmLoading ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={confirmLoading}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white bg-red-600 hover:bg-red-700 ${
                  confirmLoading ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                <FaTrash />
                {confirmLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </VehicleLayoutWrapper>
  );
};

export default VehicleSubAssemblyMaster;